import { useState } from "react";
import PropTypes from "prop-types";
import Checkbox from "@shared/v2/Checkbox";
import TextButton from "@shared/v2/TextButton";
import { AngleRightSolidV6 } from "@shared/v2/Icomoon";
import { DateRangeDropdown, resolveDateRange } from "@shared/v2/DateRangeDropdown";
import CustomControl from "../BusinessDashboard/Dropdown/CustomControl";
import { CustomBasicSingleValue } from "../BusinessDashboard/Dropdown/CustomSingleValue";
import CustomValueContainer from "../BusinessDashboard/Dropdown/CustomValueContainer";
import CustomDropdownIndicator from "../BusinessDashboard/Dropdown/CustomDropdownIndicator";
import { Section, SectionLabel } from "../BusinessDashboard/Section";
import useSalesFunnel from "../BusinessDashboard/services/useSalesFunnel";
import Loading from "../../../Goals/components/Loader";
import Error from "../DashboardShared/Error";
import FunnelRing from "./FunnelRing";
import Percent from "./Percent";
import { CountsCallsTooltip, pendedTooltip, SetHeldTooltip, signedTooltip } from "./Tooltips";

const DEFAULT_DATE_RANGE_VALUE = "this_year";

// Sales Funnel is future-inclusive (counts scheduled appointments/closings), so
// current-period options expand to the full calendar period — same as the
// standalone SalesFunnelDashboard. Exclusions:
// - `custom`/`all_time`: `goToLeadView` (ring "View" buttons) only understands
//   backend range tokens.
// - `this_week`/`last_week`: not in Listings::IndexQueryReact::VALID_DATE_RANGES,
//   so Pended/Closed ring drill-downs would open unfiltered listings.
// - `yesterday`/`this_quarter`/`last_quarter`: valid backend tokens on all rings,
//   but kept excluded to match the pre-Phase-2 dropdown until the full canonical
//   set is offered consistently across dashboards.
// Re-enable these once Listings::IndexQueryReact::VALID_DATE_RANGES and the
// close_date/status_changed scopes cover the missing week ranges.
const DATE_RANGE_CONFIG = {
  disableFutureDates: false,
  defaultOption: DEFAULT_DATE_RANGE_VALUE,
  excludeOptions: [
    "all_time",
    "custom",
    "yesterday",
    "this_week",
    "last_week",
    "this_quarter",
    "last_quarter",
  ],
};

const INITIAL_DATE_RANGE = resolveDateRange(DEFAULT_DATE_RANGE_VALUE);

const SalesFunnel = ({
  agentId,
  agentPersonId,
  agentUuid,
  customAction,
  customDateRange,
  customDateToken,
  customFilters,
  customTitle,
}) => {
  const [filters, setFilters] = useState({
    buyer: true,
    seller: true,
    buyerSeller: true,
    na: true,
  });
  const updateFilter = (name) => () => setFilters((prev) => ({ ...prev, [name]: !prev[name] }));

  const [internalDateRangeValue, setInternalDateRangeValue] = useState(DEFAULT_DATE_RANGE_VALUE);
  const [internalDateRange, setInternalDateRange] = useState(INITIAL_DATE_RANGE);

  const handleInternalDateRangeChange = (value, resolvedRange) => {
    setInternalDateRangeValue(value);
    setInternalDateRange(resolvedRange);
  };

  const effectiveDateRange = customDateRange || internalDateRange;
  const effectiveDateToken = customDateRange ? customDateToken : internalDateRangeValue;

  const { data, error, loading } = useSalesFunnel({
    agentUuid,
    dateRange: effectiveDateRange,
    filters: customFilters || filters,
  });

  return (
    <Section className="tw-gap-[16px]">
      <SectionLabel className="tw-flex tw-flex-wrap tw-justify-between tw-gap-[16px]">
        <div className="tw-flex tw-flex-wrap tw-gap-[16px]">
          <span>{customTitle || "Sales Funnel"}</span>
          {!customTitle && (
            <>
              <Checkbox label="Buyer" checked={filters.buyer} onChange={updateFilter("buyer")} />
              <Checkbox label="Seller" checked={filters.seller} onChange={updateFilter("seller")} />
              <Checkbox
                label="Buyer/Seller"
                checked={filters.buyerSeller}
                onChange={updateFilter("buyerSeller")}
              />
            </>
          )}
        </div>
        <div className="tw-flex tw-items-center">
          {!customDateRange && (
            <>
              <DateRangeDropdown
                mode="standard"
                config={DATE_RANGE_CONFIG}
                onChange={handleInternalDateRangeChange}
                components={{
                  Control: CustomControl,
                  SingleValue: CustomBasicSingleValue,
                  ValueContainer: CustomValueContainer,
                  DropdownIndicator: CustomDropdownIndicator,
                }}
              />
              <span className=" tw-h-[80%] tw-ml-[8px] tw-mr-[4px] tw-border-solid tw-border-[1px] tw-border-y-0 tw-border-r-0 tw-border-gray-10" />
            </>
          )}
          {customAction && customAction}
          {!customAction && (
            <TextButton
              className="tw-inline-flex tw-items-center tw-gap-[4px]"
              schema="sentence"
              size="medium"
              onClick={() => window.open("/settings/goals", "_blank")}
            >
              Goal Settings <AngleRightSolidV6 />
            </TextButton>
          )}
        </div>
      </SectionLabel>
      <div className="tw-overflow-x-auto tw-flex tw-gap-[4px] tw-pt-[44px]">
        <FunnelRing
          filters={filters}
          title="Called"
          tooltip={<CountsCallsTooltip />}
          data={data.calls}
          dateRange={effectiveDateToken}
          agentId={agentId}
        />
        <Percent tooltip="of Called moved to Contacted" value={`${data.callsContactedRatio}%`} />
        <FunnelRing
          filters={filters}
          title="Contacted"
          tooltip={<CountsCallsTooltip talked />}
          data={data.contacted}
          dateRange={effectiveDateToken}
          agentId={agentId}
        />
        <Percent tooltip="of Contacted moved to Appt. Set" value={`${data.contactedApptSetRatio}%`} />
        <FunnelRing
          filters={filters}
          title="Appt. Set"
          data={data.apptSet}
          tooltip={<SetHeldTooltip />}
          dateRange={effectiveDateToken}
          agentId={agentId}
        />
        <Percent tooltip="of Appt. Set moved to Appt. Held" value={`${data.apptSetApptHeldRatio}%`} />
        <FunnelRing
          filters={filters}
          title="Appt. Held"
          data={data.apptHeld}
          tooltip={<SetHeldTooltip />}
          dateRange={effectiveDateToken}
          agentId={agentId}
        />
        <Percent tooltip="of Appt. Held moved to Signed" value={`${data.apptHeldApptSignedRatio}%`} />
        <FunnelRing
          filters={filters}
          title="Signed"
          data={data.apptSigned}
          tooltip={signedTooltip}
          dateRange={effectiveDateToken}
          agentId={agentId}
        />
        <Percent tooltip="of Signed moved to Pended" value={`${data.apptSignedPendedRatio}%`} />
        <FunnelRing
          filters={filters}
          title="Pended"
          tooltip={pendedTooltip}
          data={data.pended}
          dateRange={effectiveDateToken}
          agentId={agentPersonId}
        />
        <Percent tooltip="of Pended moved to Closed" value={`${data.pendedClosedRatio}%`} />
        <FunnelRing
          filters={filters}
          title="Closed"
          data={data.closed}
          dateRange={effectiveDateToken}
          agentId={agentPersonId}
        />
      </div>
      {loading && <Loading />}
      <Error show={Boolean(error)} message={error} />
    </Section>
  );
};

SalesFunnel.propTypes = {
  agentId: PropTypes.number,
  agentPersonId: PropTypes.number,
  agentUuid: PropTypes.string,
  customAction: PropTypes.node,
  customDateRange: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ startAt: PropTypes.string, endAt: PropTypes.string }),
  ]),
  customDateToken: PropTypes.string,
  customFilters: PropTypes.shape(),
  customTitle: PropTypes.string,
};

SalesFunnel.defaultProps = {
  agentId: undefined,
  agentPersonId: undefined,
  agentUuid: undefined,
  customAction: undefined,
  customDateRange: undefined,
  customDateToken: undefined,
  customFilters: undefined,
  customTitle: undefined,
};

export default SalesFunnel;
